import React, { useMemo, useState } from "react";
import { Quote, ExternalLink } from "lucide-react";
import LanguageInsight from "./LanguageInsight";

const FILTERS = [
  { id: "all", label: "All" },
  { id: "positive", label: "Positive" },
  { id: "concern", label: "Concerns" },
  { id: "sdnp", label: "Landscape / SDNP" },
];

export default function EvidenceTab({ workshops, onSelect }) {
  const [filter, setFilter] = useState("all");
  const [query, setQuery] = useState("");

  const quotes = useMemo(() => {
    const out = [];
    for (const w of workshops) {
      for (const p of w.positive_phrases || []) {
        out.push({
          text: p,
          kind: "positive",
          workshop: w,
        });
      }
      for (const p of w.cautionary_phrases || []) {
        const isSdnp = /sdnp|park|landscape|planning|designation|national park/i.test(
          p
        );
        out.push({
          text: p,
          kind: isSdnp ? "sdnp" : "concern",
          workshop: w,
        });
      }
    }
    let list = out;
    if (filter !== "all") list = list.filter((q) => q.kind === filter);
    if (query.trim()) {
      const q = query.toLowerCase();
      list = list.filter(
        (item) =>
          item.text.toLowerCase().includes(q) ||
          item.workshop.workshop_name.toLowerCase().includes(q)
      );
    }
    return list;
  }, [workshops, filter, query]);

  return (
    <div className="space-y-10" data-testid="evidence-tab">
      <LanguageInsight workshops={workshops} />

      <div>
        <h2 className="text-xl font-semibold tracking-tight text-gray-900">
          Evidence · Quote bank
        </h2>
        <p className="text-sm text-gray-600 mt-1">
          Direct phrases extracted from the workshop reports. Use the filters
          to isolate positive language, concerns, or landscape/SDNP references.
        </p>
      </div>

      <div className="bg-white border border-gray-200 p-4 flex flex-col md:flex-row gap-3 md:items-center">
        <input
          data-testid="evidence-search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search quotes or workshop name…"
          className="flex-1 border border-gray-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#2F4F4F]/30 focus:border-[#2F4F4F]"
        />
        <div className="flex gap-1 flex-wrap">
          {FILTERS.map((f) => (
            <button
              key={f.id}
              data-testid={`evidence-filter-${f.id}`}
              onClick={() => setFilter(f.id)}
              className={
                "px-3 py-2 text-xs border transition-colors " +
                (filter === f.id
                  ? "bg-[#2F4F4F] text-white border-[#2F4F4F]"
                  : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50")
              }
            >
              {f.label}
            </button>
          ))}
        </div>
        <span
          className="text-xs text-gray-500 font-mono"
          data-testid="evidence-count"
        >
          {quotes.length} quote{quotes.length === 1 ? "" : "s"}
        </span>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        {quotes.map((q, i) => (
          <article
            key={`${q.workshop.id}-${q.kind}-${i}`}
            className="bg-white border border-gray-200 p-5 flex flex-col gap-3"
            data-testid={`quote-card-${i}`}
          >
            <div className="flex items-start gap-2">
              <Quote
                className={
                  "h-4 w-4 mt-0.5 flex-shrink-0 " +
                  (q.kind === "positive"
                    ? "text-emerald-600"
                    : q.kind === "sdnp"
                    ? "text-slate-600"
                    : "text-rose-600")
                }
              />
              <p className="text-sm text-gray-900 leading-relaxed">
                {q.text}
              </p>
            </div>
            <div className="flex items-center justify-between text-xs text-gray-500 border-t border-gray-100 pt-3">
              <button
                onClick={() => onSelect(q.workshop.id)}
                className="text-[#2F4F4F] hover:underline text-left"
                data-testid={`quote-open-${i}`}
              >
                {q.workshop.workshop_name}
                {q.workshop.parish_or_town
                  ? ` · ${q.workshop.parish_or_town}`
                  : ""}
              </button>
              {q.workshop.source_url ? (
                <a
                  href={q.workshop.source_url}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1 text-gray-500 hover:text-gray-800"
                >
                  PDF <ExternalLink className="h-3 w-3" />
                </a>
              ) : null}
            </div>
          </article>
        ))}
        {quotes.length === 0 ? (
          <div className="md:col-span-2 p-10 bg-white border border-dashed border-gray-300 text-center text-sm text-gray-500">
            No quotes match the current filters.
          </div>
        ) : null}
      </div>
    </div>
  );
}
